import React, { useState } from "react";

import classNames from "classnames";
import { capitalize } from "lodash";
import { Badge, Form, Table } from "react-bootstrap";
import Select from "react-select";

import { useUmpireInfoHandlers } from "../hooks/useUmpireInfoHandlers";
import { UmpireProps } from "../types/umpire";

import { CheckIcon } from "../../../../../../lib/icons";
import {
    defaultDateFormat,
    defaultDateTimeFormat,
} from "../../../../../../lib/utils";
import { TUserStatus } from "../../../../../../types/api";
import { AsyncStatuses } from "../../../../../../types/shared";

const UmpireInfo = ({ umpire }: UmpireProps): React.JSX.Element => {
    const [umpireStatus, setUmpireStatus] = useState<TUserStatus>(
        umpire.status,
    );
    const [updateStatus, setUpdateStatus] = useState<AsyncStatuses>(undefined);
    const { handleCommentsChange, handleStatusChange } =
        useUmpireInfoHandlers(setUpdateStatus);

    return (
        <Form>
            <Table className={classNames("umpireData")}>
                <tbody>
                    <tr>
                        <th scope="row" colSpan={2}>
                            <b>Umpire Info</b>
                        </th>
                    </tr>
                    <tr>
                        <th scope="row">Date Registered</th>
                        <td>
                            {defaultDateTimeFormat.format(
                                new Date(umpire.signupDate),
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">Address</th>
                        <td>
                            {umpire.meta.address}
                            <br />
                            {umpire.meta.address2 && (
                                <>
                                    {umpire.meta.address2}
                                    <br />
                                </>
                            )}
                            {umpire.meta.city}, {umpire.meta.state}{" "}
                            {umpire.meta.zipCode}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">Phone</th>
                        <td>
                            {umpire.meta.phoneNumbers.primary} (primary)
                            {umpire.meta.phoneNumbers.secondary && (
                                <>
                                    <br />
                                    {umpire.meta.phoneNumbers.secondary}{" "}
                                    (secondary)
                                </>
                            )}
                            {umpire.meta.phoneNumbers.other && (
                                <>
                                    <br />
                                    {umpire.meta.phoneNumbers.other} (other)
                                </>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">Email</th>
                        <td>
                            <a href={`mailto:${umpire.email}`}>
                                {umpire.email}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th scope="row">DOB</th>
                        <td>{umpire.meta.dob}</td>
                    </tr>
                    <tr>
                        <th scope="row">Race</th>
                        <td>{umpire.meta.race}</td>
                    </tr>
                    <tr>
                        <th scope="row">Gender</th>
                        <td>{umpire.meta.gender}</td>
                    </tr>
                    <tr>
                        <th scope="row">SS#</th>
                        <td>{umpire.meta.ssn}</td>
                    </tr>
                    <tr>
                        <th scope="row">Status</th>
                        <td>
                            <Select
                                className={classNames("umpireStatus")}
                                isClearable={false}
                                options={(
                                    [
                                        "active",
                                        "pending",
                                        "denied",
                                        "inactive",
                                    ] as TUserStatus[]
                                ).map(s => ({
                                    value: s,
                                    label: capitalize(s),
                                }))}
                                value={{
                                    value: umpireStatus,
                                    label: capitalize(umpireStatus),
                                }}
                                onChange={v =>
                                    v &&
                                    v.value !== umpireStatus &&
                                    handleStatusChange(
                                        umpire.id,
                                        v.value,
                                    ).finally(() => setUmpireStatus(v.value))
                                }
                            />
                            <Badge
                                bg={
                                    updateStatus === "success"
                                        ? "success"
                                        : updateStatus === "failed"
                                          ? "danger"
                                          : "info"
                                }
                                className={classNames({
                                    hidden:
                                        !updateStatus ||
                                        updateStatus === "pending",
                                    visible:
                                        updateStatus === "success" ||
                                        updateStatus === "failed",
                                })}
                            >
                                <CheckIcon />
                                {updateStatus === "success" && "Changes saved"}
                                {updateStatus === "failed" &&
                                    "Changes failed to save"}
                            </Badge>
                        </td>
                    </tr>
                    {umpire.status === "active" &&
                        umpire.confirmDate &&
                        umpire.confirmedBy && (
                            <>
                                <tr>
                                    <th scope="row">Approval Date</th>
                                    <td>
                                        {defaultDateFormat.format(
                                            new Date(umpire.confirmDate),
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row">Approved By</th>
                                    <td>{umpire.confirmedBy}</td>
                                </tr>
                            </>
                        )}
                    <tr>
                        <th scope="row">Admin Comments</th>
                        <td>
                            <Form.Control
                                as="textarea"
                                name="comments"
                                rows={4}
                                value={umpire.comments || ""}
                                onChange={e => {
                                    handleCommentsChange({
                                        umpire: umpire,
                                        comments: e.target.value,
                                    });
                                }}
                            />
                        </td>
                    </tr>
                </tbody>
            </Table>
        </Form>
    );
};

export default UmpireInfo;
